import React, { FC } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button } from '@bphxd/ds-core-react';
import { EMPTY_PURCHASE_ORDER } from '../../../lib/constants';
import { TPurchaseOrder } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { renderOptions } from '../../../lib/util/render';
import {
  PURCHASE_ORDER_STATUS_ENUM,
  PURCHASE_ORDER_TYPE_ENUM,
} from '../../../lib/constants/dbConstants';
import {
  PurchaseOrderStatusEnum,
  PurchaseOrderTypeEnum,
} from '../../../graph-queries/__generated__/graphql';

const PurchaseOrderForm: FC<{
  defaultValues?: TPurchaseOrder;
  onSubmit: (purchaesOrder: TPurchaseOrder) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const asset = useSelectedAsset();
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string> & {
      type: PurchaseOrderTypeEnum;
      status: PurchaseOrderStatusEnum;
    };

    const purchaseOrder: TPurchaseOrder = {
      ...EMPTY_PURCHASE_ORDER,
      ...inputData,
      assetId: asset?.assetId || -1,
      purchaseOrderId: parseInt(inputData.purchaseOrderId),
    };
    if (defaultValues?.purchaseOrderId)
      purchaseOrder.purchaseOrderId = defaultValues.purchaseOrderId;

    onSubmit(purchaseOrder)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };
  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-12">
        <FormGroup>
          <Label for="po-number">PO Number</Label>
          <Input
            type="text"
            name="poNumber"
            id="po-number"
            required
            defaultValue={defaultValues?.poNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="type">Type</Label>
          <Input
            type="select"
            name="type"
            id="type"
            className="form-select-placeholder-hack"
            role="combobox"
            required
            defaultValue={defaultValues?.type || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(PURCHASE_ORDER_TYPE_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="purchase-order-status">Status</Label>
          <Input
            type="select"
            name="status"
            id="purchase-order-status"
            required
            className="form-select-placeholder-hack"
            role="combobox"
            defaultValue={defaultValues?.status || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(PURCHASE_ORDER_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="vendor" className="label-optional">
            Vendor
          </Label>
          <Input type="text" name="vendor" id="vendor" defaultValue={defaultValues?.vendor || ''} />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="purchase-order-comments" className="label-optional">
            Comments
          </Label>
          <Input
            type="textarea"
            name="comments"
            id="purchase-order-comments"
            defaultValue={defaultValues?.comments || ''}
          />
        </FormGroup>
      </div>
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new purchase order
          </Button>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default PurchaseOrderForm;
